import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Save, Loader2, Lock, BookX } from "lucide-react";
import { NoaPendingAlert } from "@/components/NoaPendingAlert";
import { GradesAiImport } from "@/components/GradesAiImport";

const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(String(v).replace(",", ".")));
const fmtNota = (v) => (v == null ? "—" : Number(v).toFixed(1).replace(".", ","));
const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

function ChangeMeta({ meta, testId }) {
  if (!meta) return null;
  return (
    <div className="mx-auto mt-1 max-w-[120px] truncate text-[10px] leading-tight text-slate-400" title={`Alterado por ${meta.by} em ${new Date(meta.at).toLocaleString("pt-BR")}`} data-testid={testId}>
      {meta.by?.split(" (")[0]} • {fmtWhen(meta.at)}
    </div>
  );
}

function Filters({ opts, subject, setSubject, term, setTerm }) {
  return (
    <Card className="mb-4 grid min-w-0 grid-cols-1 gap-3 p-4 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label>Disciplina</Label>
        <Select value={subject} onValueChange={setSubject}>
          <SelectTrigger data-testid="editor-subject-select"><SelectValue placeholder="Selecione a disciplina" /></SelectTrigger>
          <SelectContent>{opts.subjects.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`editor-subject-${s.id}`}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label>Trimestre</Label>
        <Select value={String(term)} onValueChange={(v) => setTerm(Number(v))}>
          <SelectTrigger data-testid="editor-term-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Array.from({ length: opts.num_terms }, (_, i) => i + 1).map((t) => <SelectItem key={t} value={String(t)} data-testid={`editor-term-${t}`}>{t}º trimestre</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </Card>
  );
}

function GradeTable({ data, edits, setCell }) {
  const cols = data.assessments;
  return (
    <div className="overflow-x-auto">
      <Table data-testid="editor-table">
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[180px]">Estudante</TableHead>
            {cols.map((a) => <TableHead key={a.id} className="min-w-[110px] text-center">{a.name}<div className="text-[10px] font-normal text-slate-400">0 a {a.max_value}{a.is_recovery ? " • só se nota < 5,0" : ""}</div></TableHead>)}
            <TableHead className="text-center">Nota do trimestre</TableHead>
            <TableHead className="min-w-[110px] text-center">Faltas<div className="text-[10px] font-normal text-slate-400">lançadas</div></TableHead>
            <TableHead className="text-center">Faltas<div className="text-[10px] font-normal text-slate-400">no diário</div></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.rows.map((r) => {
            const e = edits[r.student_id] || {};
            return (
              <TableRow key={r.student_id} data-testid={`editor-row-${r.student_id}`}>
                <TableCell><div className="font-medium text-slate-800">{r.full_name}</div><div className="text-xs text-slate-400">{r.registration}</div></TableCell>
                {cols.map((a) => {
                  const v = e.grades?.[a.id] ?? (r.grades[a.id] ?? "");
                  const vals = cols.filter((c) => !c.is_recovery).map((c) => e.grades?.[c.id] ?? r.grades[c.id]).filter((x) => x !== "" && x != null);
                  const base = vals.length ? vals.reduce((s, x) => s + Number(x), 0) : null;
                  const noaOff = a.is_recovery && (base == null || base >= 5);
                  return (
                    <TableCell key={a.id} className="text-center">
                      <Input type="number" min={0} max={a.max_value} step="0.1" value={v} className="mx-auto h-9 w-20 text-center" disabled={noaOff && v === ""}
                        title={noaOff ? "NOA só é lançada quando a nota do trimestre é menor que 5,0" : undefined}
                        onChange={(ev) => setCell(r.student_id, "grade", ev.target.value, a.id)} data-testid={`grade-input-${r.student_id}-${a.id}`} />
                      {noaOff && v !== "" && <div className="text-[10px] text-red-600" data-testid={`noa-warning-${r.student_id}`}>Apague: nota ≥ 5,0</div>}
                      <ChangeMeta meta={r.grades_meta?.[a.id]} testId={`grade-meta-${r.student_id}-${a.id}`} />
                    </TableCell>
                  );
                })}
                <TableCell className={`text-center font-semibold ${r.final != null && r.final < data.passing_grade ? "text-red-600" : "text-slate-800"}`} data-testid={`final-${r.student_id}`}>{fmtNota(r.final)}</TableCell>
                <TableCell className="text-center">
                  <Input type="number" min={0} step="1" value={e.faltas ?? (r.faltas ?? "")} className="mx-auto h-9 w-20 text-center"
                    onChange={(ev) => setCell(r.student_id, "faltas", ev.target.value)} data-testid={`faltas-input-${r.student_id}`} />
                  <ChangeMeta meta={r.faltas_meta} testId={`faltas-meta-${r.student_id}`} />
                </TableCell>
                <TableCell className="text-center text-slate-500">{r.diary_faltas}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function NotasFaltasEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [opts, setOpts] = useState(null);
  const [subject, setSubject] = useState("");
  const [term, setTerm] = useState(1);
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [noaKey, setNoaKey] = useState(0);

  useEffect(() => {
    api.get(`/classes/${id}/editor/options`).then((r) => { setOpts(r.data); if (r.data.subjects[0]) setSubject(r.data.subjects[0].id); })
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  }, [id]);

  const load = useCallback(() => {
    if (!subject) return;
    setData(null); setEdits({});
    api.get(`/classes/${id}/editor`, { params: { subject_id: subject, term_order: term } })
      .then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  }, [id, subject, term]);
  useEffect(() => { load(); }, [load]);

  const setCell = (sid, kind, value, aid) => setEdits((prev) => {
    const cur = { ...(prev[sid] || {}) };
    if (kind === "grade") cur.grades = { ...(cur.grades || {}), [aid]: value };
    else cur.faltas = value;
    return { ...prev, [sid]: cur };
  });

  const save = async () => {
    const rows = Object.entries(edits).map(([sid, e]) => {
      const orig = data.rows.find((r) => r.student_id === sid);
      const grades = Object.fromEntries(Object.entries(e.grades || {}).map(([k, v]) => [k, toNum(v)]));
      return { student_id: sid, grades, faltas: "faltas" in e ? (toNum(e.faltas) === null ? null : Math.round(toNum(e.faltas))) : orig.faltas };
    });
    if (!rows.length) { toast.info("Nenhuma alteração para salvar"); return; }
    setSaving(true);
    try {
      const r = await api.put(`/classes/${id}/editor`, { subject_id: subject, term_order: term, rows });
      toast.success(`Salvo: ${r.data.notas_alteradas} nota(s) e ${r.data.faltas_alteradas} falta(s) atualizadas`);
      load(); setNoaKey((k) => k + 1);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setSaving(false); }
  };

  const dirty = Object.keys(edits).length > 0;
  return (
    <div data-testid="notas-faltas-editor-page">
      <PageHeader title={`Notas e faltas — Turma ${opts?.class?.name || ""}`} subtitle={`Nota do trimestre = Trabalho (0 a 2) + Teste (0 a 3) + Prova (0 a 5) • aprovação ${fmtNota(data?.passing_grade ?? 5)} • abaixo de cada nota: quem alterou por último e quando`}>
        <Button variant="outline" onClick={() => navigate("/turmas")} data-testid="editor-back-btn"><ArrowLeft className="mr-2 h-4 w-4" />Turmas</Button>
        {opts?.subjects?.length > 0 && <GradesAiImport classId={id} onDone={() => { load(); setNoaKey((k) => k + 1); }} />}
        {data && <Button onClick={save} disabled={saving || !dirty} className="bg-orange-500 hover:bg-orange-600" data-testid="editor-save-btn">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Salvar alterações</Button>}
      </PageHeader>
      {!opts ? <p className="py-10 text-center text-sm text-slate-400">Carregando...</p>
        : !opts.subjects.length ? (
          <EmptyState icon={BookX} title="Nenhuma disciplina disponível"
            description="Você só pode editar notas e faltas das disciplinas que leciona nesta turma." />
        ) : (
          <>
            <NoaPendingAlert classId={id} reloadKey={noaKey} onPick={(s, t) => { setSubject(s); setTerm(t); }} />
            <Filters opts={opts} subject={subject} setSubject={setSubject} term={term} setTerm={setTerm} />
            {data?.diary_closed && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800" data-testid="editor-diary-closed">
                <Lock className="h-4 w-4" />Diário fechado neste trimestre. Somente a gestão pode salvar alterações.
              </div>
            )}
            <Card className="min-w-0 p-2 sm:p-4">
              {!data ? <p className="py-10 text-center text-sm text-slate-400">Carregando...</p>
                : !data.rows.length ? <p className="py-10 text-center text-sm text-slate-500">Nenhum estudante matriculado nesta turma.</p>
                : <GradeTable data={data} edits={edits} setCell={setCell} />}
            </Card>
          </>
        )}
    </div>
  );
}
